import { useEffect, useState } from "react";
import { Button, Drawer } from "../../UI";
import MessageBox from "../../UI/Chat/MessageBox/MessageBox";
import MessageListContainer from "../../UI/Chat/MessagesListContainer/MessageListContainer";
import "./AIChat.css";
import { AIToolServices } from "../../../Services/AIToolServices";
import useSessionState from "../../../Hooks/useSessionState";
import ChatInput from "../../UI/Chat/ChatInput/ChatInput";
import { useTranslation } from "react-i18next";

const AIChat = () => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useSessionState("AI-ChatId");
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [isLoadingNewMessage, setIsLoadingNewMessage] = useState(false);

  const isAnyLoading = isLoadingChat || isLoadingNewMessage;

  useEffect(() => {
    if (!show || !chatId) return;
    generateChat(chatId);
  }, [show]);

  const generateChat = async (id) => {
    try {
      setIsLoadingChat(true);
      await fetchNewMessages(id);
    } finally {
      setIsLoadingChat(false);
    }
  };

  const fetchNewMessages = async (id) => {
    const res = await AIToolServices.getAssistantChatMessage({
      chatId: id || chatId,
      chatAITypeEnum: 2,
    });
    setMessages(res?.data?.results || []);
  };

  const handleSendMessage = async (data) => {
    const { message, files = [] } = data;
    setMessages((x) => [
      ...x,
      {
        message,
        messageType: "user",
        id: new Date(),
        fileNames: files.map((f) => f.name),
        isFile: files.length > 0,
      },
    ]);

    setIsLoadingNewMessage(true);

    try {
      const formData = new FormData();
      if (chatId) {
        formData.append("chatId", chatId);
      }
      formData.append("message", message);
      formData.append("chatAIType", 2);
      files.forEach((f) => formData.append("document", f));

      const res = await AIToolServices.postAssistantChatPrompt(formData);
      if (!chatId) setChatId(res?.data?.chatId);
      fetchNewMessages(res?.data?.chatId);
    } finally {
      setIsLoadingNewMessage(false);
    }
  };

  return (
    <>
      <Button
        className="ai-chat__show-btn"
        appearance="primary"
        onClick={() => {
          setShow(true);
        }}
      >
        {t("AI Chat")}
      </Button>
      <Drawer
        show={show}
        onHide={() => setShow(false)}
        className="ai-chat__drawer"
      >
        <Drawer.Header className="ai-chat__drawer-header">
          <Drawer.Title>{t("AI Chat")}</Drawer.Title>
          <div className="ai-chat__drawer-header-actions">
            <Button
              onClick={() => {
                setChatId();
                setMessages([]);
              }}
            >
              {t("New conversation")}
            </Button>
          </div>
        </Drawer.Header>

        <div className="ai-chat__drawer-body">
          <MessageListContainer
            isEmpty={!(messages.length > 0)}
            isLoading={isLoadingChat}
            isLoadingNewMessage={isLoadingNewMessage}
          >
            {messages.map((x) => (
              <MessageBox
                key={x.id}
                position={x.messageType === "user" ? "right" : "left"}
                message={x.message}
                showCoppyButton={x.messageType === "assistant"}
                messageFiles={x.files}
              />
            ))}
          </MessageListContainer>
        </div>
        <div className="ai-chat__drawer-footer">
          <ChatInput
            onSend={handleSendMessage}
            isLoading={isAnyLoading}
            showFileDropdown={true}
          />
        </div>
      </Drawer>
    </>
  );
};

export default AIChat;
